import React, { useMemo, useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom'
import { 
  Calculator, 
  Plus, 
  Minus, 
  Trash2, 
  User, 
  Percent,
  CreditCard,
  Banknote,
  Printer,
  ShoppingCart,
  Coffee,
  Package,
  DollarSign,
  Calendar,
  TrendingUp,
  Clock,
  Eye,
  X,
  Car,
  Search
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { posService } from '../services/posService'
import { DiscountService } from '../services/discountService'
import InvoiceGenerator from '../utils/invoiceGenerator'
import LoadingSpinner from '../components/LoadingSpinner'
import CustomDropdown from '../components/CustomDropdown'
import BranchSelector from '../components/BranchSelector'
import ConnectionStatus from '../components/ConnectionStatus'
import FirebaseErrorHandler from '../utils/firebaseErrorHandler'
import { cn } from '../utils/cn'
import { subscriptionService, customerService, washService } from '../services/firebase'
import { subscriptionPlanService } from '@/services/subscriptionPlanService'
import { productService } from '@/services/productService'
import { checkoutRequestService } from '@/services/checkoutRequestService'
import { washNeedsCashierPayment } from '@/utils/paymentStatus'
import { washCatalogService } from '@/services/washCatalogService'
import {
  washPackages as staticWashPackages,
  formatWashSelectionLabel,
  isTierPOA,
} from '@/config/washCatalog'

// Products for Manual POS are managed in-app via /products (Firestore).

export default function ManualPOSPage() {
  const { currentUser, userProfile } = useAuth()
  const { currentBranch, isViewingAllBranches, getEffectiveBranchId } = useBranch()
  const location = useLocation()
  const navigate = useNavigate()
  const { washId } = useParams()

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search])
  const preselectCustomerId = qs.get('customerId')
  const preselectSubPlan = qs.get('subPlan')
  const preselectTab = qs.get('tab')
  
  const [cart, setCart] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [discountPercentage, setDiscountPercentage] = useState(0)
  const [discountReason, setDiscountReason] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [isProcessing, setIsProcessing] = useState(false)
  const [customers, setCustomers] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [serviceSearch, setServiceSearch] = useState('')
  const [customItem, setCustomItem] = useState({ name: '', price: '', description: '' })
  const [showCustomItem, setShowCustomItem] = useState(false)
  const [transactions, setTransactions] = useState([])
  const [subscriptionPlans, setSubscriptionPlans] = useState([])
  const [products, setProducts] = useState([])
  const [washPackages, setWashPackages] = useState(staticWashPackages)
  const [customerSearch, setCustomerSearch] = useState('')
  const [showAddCustomer, setShowAddCustomer] = useState(false)
  const [savingCustomer, setSavingCustomer] = useState(false)
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    surname: '',
    phone: '',
    email: '',
  })
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [showTransactionModal, setShowTransactionModal] = useState(false)
  const PAGE_SIZE = 25
  const [txPage, setTxPage] = useState(1)
  const [transactionForm, setTransactionForm] = useState({
    paymentNotes: '',
    salesAgent: '',
    commissionPercentage: 0,
    discountType: 'percentage', // 'percentage' or 'fixed'
    discountValue: 0,
    discountReason: '',
    paymentMethod: 'cash',
    referenceNumber: '',
    customerNotes: ''
  })
  const [checkoutCode, setCheckoutCode] = useState('')
  const [loadingCheckout, setLoadingCheckout] = useState(false)
  const [openQueueWashes, setOpenQueueWashes] = useState([])
  const [loadingOpenQueue, setLoadingOpenQueue] = useState(false)

  useEffect(() => {
    loadCustomers()
  }, [])

  useEffect(() => {
    loadTransactions()
  }, [currentBranch?.id, currentBranch?.isAllBranches])

  /** Branch scope for POS sales: staff/managers always their branch; owners follow selector */
  const posSalesBranchId = useMemo(() => {
    if (userProfile?.role === 'owner') {
      if (!currentBranch || currentBranch.isAllBranches) return null // all branches
      return currentBranch.id
    }
    return getEffectiveBranchId()
  }, [currentBranch?.id, currentBranch?.isAllBranches, userProfile?.role, getEffectiveBranchId])

  const loadOpenWashingQueue = async () => {
    const branchId = getEffectiveBranchId()
    if (!branchId || branchId === 'all-branches') {
      setOpenQueueWashes([])
      return
    }
    setLoadingOpenQueue(true)
    try {
      const washes = await washService.getWashesByBranch(branchId)
      const open = (washes || []).filter(
        (w) =>
          w.status !== 'picked_up' &&
          w.status !== 'cancelled' &&
          washNeedsCashierPayment(w)
      )
      setOpenQueueWashes(open.slice(0, 40))
    } catch (e) {
      console.error(e)
      setOpenQueueWashes([])
    } finally {
      setLoadingOpenQueue(false)
    }
  }

  useEffect(() => {
    loadOpenWashingQueue()
  }, [currentBranch?.id, currentBranch?.isAllBranches])

  const openWashInPos = (id) => {
    navigate(`/pos/${id}`)
  }

  useEffect(() => {
    ;(async () => {
      try {
        await washCatalogService.ensureInitialized()
        const doc = await washCatalogService.get()
        if (doc?.packages?.length) setWashPackages(doc.packages)
      } catch (e) {
        console.error(e)
      }
    })()
  }, [])

  useEffect(() => {
    if (!currentBranch?.id) return
    subscriptionPlanService
      .listPlans(currentBranch.id)
      .then((plans) => setSubscriptionPlans(plans.filter((p) => p.isActive !== false)))
      .catch((e) => {
        console.error(e)
      })
  }, [currentBranch?.id])

  useEffect(() => {
    if (!currentBranch?.id) return
    ;(async () => {
      try {
        const list = await productService.listByBranch(currentBranch.id)
        setProducts(list.filter((p) => p.isActive !== false))
      } catch (e) {
        console.error(e)
      }
    })()
  }, [currentBranch?.id])

  const loadCustomers = async () => {
    try {
      const customersData = await FirebaseErrorHandler.retryOperation(
        () => posService.getCustomers()
      )
      setCustomers(customersData)
    } catch (error) {
      const message = FirebaseErrorHandler.logError(error, 'Loading customers')
      console.error('Error loading customers:', message)
    }
  }

  // If opened via /pos/:washId, prefill cart + customer from wash record
  useEffect(() => {
    if (!washId) return
    // Guard: /pos/queue used to be caught as washId on some routers
    if (washId === 'queue') {
      navigate('/pos-queue', { replace: true })
      return
    }
    ;(async () => {
      try {
        const wash = await washService.getWashById(washId)
        if (!wash) return

        // Prefill customer once customers are loaded (or immediately for walk-in)
        if (wash.customerId && wash.customerId !== 'walk-in') {
          setSelectedCustomer((prev) => prev || customers.find((c) => c.id === wash.customerId) || prev)
        }

        // Add a "Wash" line item into the cart (one-time)
        setCart((prev) => {
          const already = prev.some((x) => x.meta?.washId === washId || x.id === `wash_${washId}`)
          if (already) return prev
          const price = Number(wash.amount || 0)
          return [
            {
              id: `wash_${washId}`,
              name: wash.serviceType || 'Wash',
              price: Number.isFinite(price) ? price : 0,
              category: 'wash',
              quantity: 1,
              meta: { washId },
            },
            ...prev,
          ]
        })
      } catch (e) {
        console.error(e)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [washId, customers])

  useEffect(() => {
    if (!preselectCustomerId || customers.length === 0) return
    const found = customers.find((c) => c.id === preselectCustomerId)
    if (found) setSelectedCustomer(found)
  }, [preselectCustomerId, customers])

  useEffect(() => {
    if (!preselectSubPlan) return
    const plan =
      subscriptionPlans.find((p) => p.code === preselectSubPlan) ||
      subscriptionPlans.find((p) => p.id === preselectSubPlan)
    if (!plan) return
    setCart((prev) => {
      const already = prev.some((x) => x.category === 'subscription')
      if (already) return prev
      return [
        ...prev,
        {
          id: `subscription_${plan.code || plan.id}`,
          name: `Subscription: ${plan.name}`,
          price: Number(plan.amount || 0),
          category: 'subscription',
          quantity: 1,
          meta: {
            planId: plan.id,
            planCode: plan.code,
            washesIncluded: plan.washesIncluded,
            amount: Number(plan.amount || 0),
            durationDays: Number(plan.durationDays || 0),
            eligibleServiceIds: Array.isArray(plan.eligibleServiceIds) ? plan.eligibleServiceIds : [],
            eligibleTierIds: Array.isArray(plan.eligibleTierIds) ? plan.eligibleTierIds : [],
          },
        },
      ]
    })
  }, [preselectSubPlan, subscriptionPlans])

  useEffect(() => {
    if (preselectTab === 'subscription') setSelectedCategory('subscription')
    if (preselectTab === 'wash') setSelectedCategory('wash')
  }, [preselectTab])

  const loadCheckoutRequest = async () => {
    const code = String(checkoutCode || '').trim()
    if (!code) return
    try {
      setLoadingCheckout(true)
      const req = await checkoutRequestService.getRequestById(code)
      if (!req) {
        alert('Checkout request not found')
        return
      }
      if (req.status !== 'pending') {
        alert('Checkout request already processed')
        return
      }
      if (req.type !== 'subscription') {
        alert('Unsupported request type')
        return
      }

      const cust = customers.find((c) => c.id === req.customerId)
      if (cust) {
        setSelectedCustomer(cust)
      } else {
        try {
          // Fallback: customer might not be in this branch-loaded list
          const fetched = await customerService.getCustomerById(req.customerId)
          if (fetched?.id) {
            setSelectedCustomer(fetched)
          } else {
            setSelectedCustomer({
              id: req.customerId,
              name: req.customerName || 'Customer',
              phone: null,
              email: req.userEmail || null,
            })
          }
        } catch (e) {
          console.warn('Could not fetch customer for request:', e)
          setSelectedCustomer({
            id: req.customerId,
            name: req.customerName || 'Customer',
            phone: null,
            email: req.userEmail || null,
          })
        }
      }

      // Add subscription line to cart from request payload
      setCart((prev) => {
        const already = prev.some((x) => x.category === 'subscription')
        if (already) return prev
        return [
          ...prev,
          {
            id: `subscription_${req.planCode || req.planId}`,
            name: `Subscription: ${req.planName || 'Package'}`,
            price: Number(req.amount || 0),
            category: 'subscription',
            quantity: 1,
            meta: {
              planId: req.planId,
              planCode: req.planCode,
              washesIncluded: req.washesIncluded,
              amount: Number(req.amount || 0),
              durationDays: Number(req.durationDays || 0),
              eligibleServiceIds: Array.isArray(req.eligibleServiceIds) ? req.eligibleServiceIds : [],
              checkoutRequestId: req.id,
            },
          },
        ]
      })
      setSelectedCategory('subscription')
      alert('Loaded checkout request into cart')
    } catch (e) {
      console.error(e)
      alert('Failed to load checkout request')
    } finally {
      setLoadingCheckout(false)
    }
  }

  const loadTransactions = async () => {
    try {
      // Non-owners must always be scoped to a branch — never load all sales
      const canSeeAll =
        userProfile?.role === 'owner' && (!currentBranch || isViewingAllBranches())
      const branchFilter = canSeeAll ? null : posSalesBranchId || currentBranch?.id || userProfile?.branchId

      if (!canSeeAll && !branchFilter) {
        setTransactions([])
        return
      }

      const transactionsData = await FirebaseErrorHandler.retryOperation(() =>
        posService.getTransactions(branchFilter)
      )

      // Client-side safety: drop anything not matching the branch when scoped
      const scoped = !branchFilter
        ? transactionsData
        : (transactionsData || []).filter(
            (t) => !t.branchId || t.branchId === branchFilter
          )

      setTransactions(scoped)
    } catch (error) {
      const message = FirebaseErrorHandler.logError(error, 'Loading transactions')
      console.error('Error loading transactions:', message)
      setTransactions([])
    }
  }

  const txDate = (t) => {
    const v = t?.createdAt
    if (v && typeof v === 'object' && typeof v.toDate === 'function') return v.toDate()
    const d = new Date(v || 0)
    return Number.isNaN(d.getTime()) ? new Date(0) : d
  }

  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => txDate(b).getTime() - txDate(a).getTime())
  }, [transactions])

  const txTotalPages = Math.max(1, Math.ceil(sortedTransactions.length / PAGE_SIZE))
  const pagedTransactions = sortedTransactions.slice((txPage - 1) * PAGE_SIZE, txPage * PAGE_SIZE)

  useEffect(() => {
    setTxPage(1)
  }, [currentBranch?.id])

  useEffect(() => {
    if (txPage > txTotalPages) setTxPage(txTotalPages)
  }, [txPage, txTotalPages])

  const productItems = useMemo(
    () =>
      (products || []).map((p) => ({
        id: `product_${p.id}`,
        name: p.name,
        price: Number(p.price || 0),
        category: p.category || 'product',
        icon: '🛒',
      })),
    [products]
  )

  const washCatalogItems = useMemo(
    () =>
      (washPackages || []).flatMap((pkg) =>
        (pkg.tiers || []).map((tier) => {
          const poa = isTierPOA(tier)
          return {
            id: `washcat_${pkg.id}_${tier.id}`,
            name: formatWashSelectionLabel(pkg, tier),
            description: pkg.description || tier.label,
            price: poa ? null : Number(tier.price || 0),
            category: 'wash',
            poa,
            icon: '🚗',
            meta: {
              catalogServiceId: pkg.id,
              catalogTierId: tier.id,
              vehicleClassLabel: tier.label,
              poa,
            },
          }
        })
      ),
    [washPackages]
  )

  const categories = useMemo(() => {
    const dynamicCats = Array.from(new Set((products || []).map((p) => String(p.category || 'product'))))
      .filter(Boolean)
      .sort()
      .map((c) => ({ id: c, name: c.charAt(0).toUpperCase() + c.slice(1), icon: Package }))

    return [
      { id: 'all', name: 'All Items', icon: Package },
      { id: 'wash', name: 'Wash Catalogue', icon: Car },
      { id: 'subscription', name: 'Subscriptions', icon: TrendingUp },
      ...dynamicCats,
    ]
  }, [products])

  const categoryItems = useMemo(() => {
    if (selectedCategory === 'all') return [...washCatalogItems, ...productItems]
    if (selectedCategory === 'wash') return washCatalogItems
    if (selectedCategory === 'subscription') {
      return subscriptionPlans.map((plan) => ({
        id: `subscription_${plan.code || plan.id}`,
        name: `Subscription: ${plan.name}`,
        price: Number(plan.amount || 0),
        category: 'subscription',
        icon: '🧾',
        quantity: 1,
        meta: {
          planId: plan.id,
          planCode: plan.code,
          washesIncluded: plan.washesIncluded,
          amount: Number(plan.amount || 0),
          durationDays: Number(plan.durationDays || 0),
          eligibleServiceIds: Array.isArray(plan.eligibleServiceIds) ? plan.eligibleServiceIds : [],
          eligibleTierIds: Array.isArray(plan.eligibleTierIds) ? plan.eligibleTierIds : [],
        },
      }))
    }
    return productItems.filter((p) => (p.category || 'product') === selectedCategory)
  }, [selectedCategory, washCatalogItems, productItems, subscriptionPlans])

  const filteredItems = useMemo(() => {
    const q = serviceSearch.trim().toLowerCase()
    if (!q) return categoryItems
    return categoryItems.filter((item) => {
      const name = String(item.name || '').toLowerCase()
      const desc = String(item.description || '').toLowerCase()
      const cat = String(item.category || '').toLowerCase()
      const vehicle = String(item.meta?.vehicleClassLabel || '').toLowerCase()
      return name.includes(q) || desc.includes(q) || cat.includes(q) || vehicle.includes(q)
    })
  }, [categoryItems, serviceSearch])

  useEffect(() => {
    setServiceSearch('')
  }, [selectedCategory])

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase()
    if (!q) return customers.slice(0, 8)
    const matches = customers.filter((c) => {
      const name = (c.name || '').toLowerCase()
      const email = (c.email || '').toLowerCase()
      const phone = (c.phone || '').toLowerCase()
      return name.includes(q) || email.includes(q) || phone.includes(q)
    })
    // Prefer closer matches first (name/phone start-with)
    matches.sort((a, b) => {
      const an = (a.name || '').toLowerCase()
      const bn = (b.name || '').toLowerCase()
      const ap = (a.phone || '').toLowerCase()
      const bp = (b.phone || '').toLowerCase()
      const ae = (a.email || '').toLowerCase()
      const be = (b.email || '').toLowerCase()
      const score = (c) => {
        const n = (c.name || '').toLowerCase()
        const p = (c.phone || '').toLowerCase()
        const e = (c.email || '').toLowerCase()
        if (n.startsWith(q) || p.startsWith(q) || e.startsWith(q)) return 0
        return 1
      }
      return score(a) - score(b) || an.localeCompare(bn) || ap.localeCompare(bp)
    })
    return matches.slice(0, 12)
  }, [customers, customerSearch])

  const openAddCustomerForm = () => {
    const q = customerSearch.trim()
    const digits = q.replace(/\D/g, '')
    if (digits.length >= 8) {
      setNewCustomer({ firstName: '', surname: '', phone: q, email: '' })
    } else if (q.includes('@')) {
      setNewCustomer({ firstName: '', surname: '', phone: '', email: q })
    } else {
      const parts = q.split(/\s+/).filter(Boolean)
      setNewCustomer({
        firstName: parts[0] || '',
        surname: parts.slice(1).join(' '),
        phone: '',
        email: '',
      })
    }
    setShowAddCustomer(true)
  }

  const createPosCustomer = async (e) => {
    e?.preventDefault?.()
    const firstName = String(newCustomer.firstName || '').trim()
    const surname = String(newCustomer.surname || '').trim()
    const phoneRaw = String(newCustomer.phone || '').trim()
    const email = String(newCustomer.email || '').trim().toLowerCase()
    const name = [firstName, surname].filter(Boolean).join(' ')
    const phone = customerService.normalizePhone(phoneRaw) || phoneRaw.replace(/\s+/g, '')

    if (!firstName) {
      toast.error('Name is required')
      return
    }
    if (!phone || phone.replace(/\D/g, '').length < 9) {
      toast.error('A valid mobile number is required')
      return
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email, or leave it blank')
      return
    }

    setSavingCustomer(true)
    try {
      const existing = await customerService.getCustomerByPhone(phone)
      if (existing?.id) {
        setSelectedCustomer(existing)
        setCustomers((prev) => (prev.some((c) => c.id === existing.id) ? prev : [existing, ...prev]))
        setShowAddCustomer(false)
        setCustomerSearch('')
        toast.success(`Existing customer loaded: ${existing.name || name}`)
        return
      }

      const branchId = getEffectiveBranchId()
      const payload = {
        name,
        firstName,
        surname,
        phone,
        email: email || '',
        branchId: branchId && branchId !== 'all-branches' ? branchId : null,
        loyaltyPoints: 0,
        voucherBalance: 0,
        voucherSpendProgress: 0,
        totalWashes: 0,
        isSystemProfile: false,
        createdFrom: 'pos',
      }
      const id = await customerService.createCustomer(payload)
      const created = { ...payload, id }
      setSelectedCustomer(created)
      setCustomers((prev) => [created, ...prev])
      setShowAddCustomer(false)
      setCustomerSearch('')
      setNewCustomer({ firstName: '', surname: '', phone: '', email: '' })
      toast.success('Customer created')
    } catch (err) {
      console.error(err)
      toast.error('Could not create customer')
    } finally {
      setSavingCustomer(false)
    }
  }

  const addToCart = (item) => {
    let line = item
    if (item.poa || item.meta?.poa) {
      const entered = window.prompt(`Enter price for "${item.name}" (POA):`, '')
      if (entered == null) return
      const price = Number(entered)
      if (!Number.isFinite(price) || price <= 0) {
        alert('Please enter a valid price')
        return
      }
      line = {
        ...item,
        price,
        id: `${item.id}_${Date.now()}`,
        meta: { ...item.meta, poa: true, poaEntered: true },
      }
    }

    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === line.id)
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === line.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      }
      return [...prevCart, { ...line, quantity: 1 }]
    })
  }

  const addCustomItem = () => {
    if (!customItem.name || !customItem.price) {
      alert('Please enter both name and price for custom item')
      return
    }

    const customCartItem = {
      id: `custom_${Date.now()}`,
      name: customItem.name,
      price: parseFloat(customItem.price),
      category: 'custom',
      description: customItem.description,
      quantity: 1
    }

    setCart(prevCart => [...prevCart, customCartItem])
    setCustomItem({ name: '', price: '', description: '' })
    setShowCustomItem(false)
  }

  const updateQuantity = (itemId, change) => {
    setCart(prevCart =>
      prevCart.map(item => {
        if (item.id === itemId) {
          const newQuantity = Math.max(0, item.quantity + change)
          return newQuantity === 0 ? null : { ...item, quantity: newQuantity }
        }
        return item
      }).filter(Boolean)
    )
  }

  const removeFromCart = (itemId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId))
  }

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0)
  }

  const calculateDiscountAmount = () => {
    return (calculateSubtotal() * discountPercentage) / 100
  }

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscountAmount()
  }

  const getCartSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0)
  }

  const getCartTax = () => {
    // Prices are VAT-inclusive, so VAT is extracted as 15/115 of the inclusive total
    const incl = getCartSubtotal()
    return incl * (15 / 115)
  }

  const getCartTotal = () => {
    // VAT is already included in item prices
    return getCartSubtotal()
  }

  const getTotalRevenue = () => {
    return transactions.length
  }

  const getTodaySales = () => {
    const today = new Date().toISOString().slice(0, 10)
    return transactions.filter(t => {
      const transactionDate = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt)
      return transactionDate.toISOString().slice(0, 10) === today
    })
  }

  const getAverageSale = () => {
    if (transactions.length === 0) return 0
    const totalSum = transactions.reduce((sum, t) => sum + (t.total || 0), 0)
    return (totalSum / transactions.length).toFixed(2)
  }

  const processTransaction = () => {
    if (!currentUser) {
      alert('Please sign in to process transactions')
      return
    }

    if (!currentBranch || currentBranch.isAllBranches) {
      alert('Please select a specific branch to process transactions')
      return
    }

    if (cart.length === 0) {
      alert('Cart is empty')
      return
    }

    // Reset form and show modal
    setTransactionForm({
      paymentNotes: '',
      salesAgent: '',
      commissionPercentage: 0,
      discountType: 'percentage',
      discountValue: 0,
      discountReason: '',
      paymentMethod: 'cash',
      referenceNumber: '',
      customerNotes: ''
    })
    setShowTransactionModal(true)
  }

  const completeTransaction = async () => {
    if (transactionForm.discountValue > 0 && !transactionForm.discountReason.trim()) {
      alert('Please provide a reason for the discount')
      return
    }

    if (transactionForm.paymentMethod === 'account') {
      if (!selectedCustomer?.id || selectedCustomer.id === 'walk-in') {
        alert('Select a customer for on-account billing')
        return
      }
      if (!selectedCustomer.allowOnAccount) {
        alert('This customer is not enabled for on-account billing. Enable it under Customers.')
        return
      }
    }

    setIsProcessing(true)

    try {
      // Calculate discount based on type
      const discountAmount = transactionForm.discountType === 'percentage' 
        ? (calculateSubtotal() * transactionForm.discountValue / 100)
        : transactionForm.discountValue

      const finalTotal = calculateSubtotal() - discountAmount

      const transactionData = {
        customerId: selectedCustomer?.id || 'walk-in',
        customerName: selectedCustomer?.name || 'Walk-in Customer',
        customerEmail: selectedCustomer?.email || null,
        customerPhone: selectedCustomer?.phone || null,
        branchId: currentBranch?.isAllBranches ? 'main-branch' : currentBranch?.id,
        branchName: currentBranch?.isAllBranches ? 'Main Branch' : currentBranch?.name || 'Main Branch',
        staffId: currentUser?.uid || 'unknown',
        salesAgent: transactionForm.salesAgent,
        commissionPercentage: transactionForm.commissionPercentage,
        items: cart,
        subtotal: calculateSubtotal(),
        discountType: transactionForm.discountType,
        discountValue: transactionForm.discountValue,
        discountAmount: discountAmount,
        discountReason: transactionForm.discountReason,
        total: finalTotal,
        paymentMethod: transactionForm.paymentMethod,
        paymentNotes: transactionForm.paymentNotes,
        referenceNumber: transactionForm.referenceNumber,
        customerNotes: transactionForm.customerNotes,
        type: 'manual_pos',
        location: currentBranch?.isAllBranches ? 'Main Branch' : currentBranch?.name || 'Main Branch'
      }

      // Process transaction
      const transaction = await posService.processTransaction(transactionData)
      
      // Log discount if applied
      if (transactionForm.discountValue > 0 && discountAmount > 0) {
        await DiscountService.logDiscount({
          transactionId: transaction.id,
          type: transactionForm.discountType,
          percentage: transactionForm.discountType === 'percentage' ? transactionForm.discountValue : 0,
          amount: discountAmount,
          originalAmount: calculateSubtotal(),
          finalAmount: finalTotal,
          reason: transactionForm.discountReason,
          customerId: selectedCustomer?.id || 'walk-in',
          customerName: selectedCustomer?.name || 'Walk-in Customer',
          customerEmail: selectedCustomer?.email,
          customerPhone: selectedCustomer?.phone,
          appliedBy: currentUser?.uid || 'unknown',
          appliedByName: currentUser?.displayName || currentUser?.email || userProfile?.displayName || 'Unknown',
          appliedByRole: userProfile?.role || 'staff',
          branchId: currentBranch?.isAllBranches ? 'main-branch' : currentBranch?.id,
          branchName: currentBranch?.isAllBranches ? 'Main Branch' : currentBranch?.name,
          items: cart.map(item => ({
            id: item.id,
            name: item.name,
            category: item.category,
            quantity: item.quantity,
            price: item.price
          }))
        })
      }
      
      // Generate invoice
      const invoice = await InvoiceGenerator.generateInvoice(transactionData)
      
      // Auto-print thermal receipt
      await InvoiceGenerator.printThermalReceipt(invoice)

      const isAccountSale = transactionForm.paymentMethod === 'account'

      // If this sale is closing a wash, update payment on the wash record.
      if (washId) {
        try {
          if (isAccountSale) {
            await washService.updateWashStatus(washId, undefined, {
              paymentStatus: 'on_account',
              paymentMethod: 'account',
              posTransactionId: transaction?.id || null,
              invoiceId: invoice?.id || null,
            })
          } else {
            await washService.updateWashStatus(washId, undefined, {
              paymentStatus: 'paid',
              paymentMethod: transactionForm.paymentMethod,
              paidAt: new Date(),
              posTransactionId: transaction?.id || null,
              invoiceId: invoice?.id || null,
            })
          }
        } catch (e) {
          console.error('Failed to update wash payment:', e)
        }
      }

      if (isAccountSale && selectedCustomer?.id) {
        try {
          await customerService.adjustAccountBalance(selectedCustomer.id, finalTotal)
        } catch (e) {
          console.error('Failed to update account balance:', e)
        }
      }

      // If a subscription was sold, create/attach it to the customer account
      const subscriptionItem = cart.find((x) => x.category === 'subscription')
      if (subscriptionItem && selectedCustomer?.id && selectedCustomer.id !== 'walk-in') {
        const meta = subscriptionItem.meta || {}
        const startDate = new Date()
        let expiryDate = new Date(startDate)

        if (Number(meta.durationDays || 0) > 0) {
          expiryDate = new Date(startDate.getTime() + Number(meta.durationDays) * 24 * 60 * 60 * 1000)
        } else {
          // fallback for older hardcoded plans
          expiryDate.setMonth(expiryDate.getMonth() + 12)
        }

        await subscriptionService.createSubscription({
          customerId: selectedCustomer.id,
          customerName: selectedCustomer.name,
          customerPhone: selectedCustomer.phone,
          planType: meta.planCode || meta.planId || 'monthly',
          amount: Number(meta.amount || subscriptionItem.price || 0),
          startDate,
          expiryDate,
          washesIncluded: meta.washesIncluded ?? 'unlimited',
          eligibleServiceIds: Array.isArray(meta.eligibleServiceIds) ? meta.eligibleServiceIds : [],
          eligibleTierIds: Array.isArray(meta.eligibleTierIds) ? meta.eligibleTierIds : [],
          washesUsed: 0,
          isActive: true,
          branchId: currentBranch?.isAllBranches ? 'main-branch' : currentBranch?.id,
          sourceTransactionId: transaction?.id || null,
          sourceInvoiceId: invoice?.id || null,
          createdAt: new Date(),
        })

        await customerService.updateCustomer(selectedCustomer.id, {
          hasActiveSubscription: true,
        })

        // Mark checkout request as processed (if this was loaded from customer app)
        if (meta.checkoutRequestId) {
          try {
            await checkoutRequestService.markProcessed(meta.checkoutRequestId, { processedByUid: currentUser?.uid })
          } catch (e) {
            console.error('Failed to mark checkout request processed:', e)
          }
        }

        // Remove query params so refresh doesn't re-add subscription
        navigate('/pos', { replace: true })
      }
      
      // Reset form
      setCart([])
      setSelectedCustomer(null)
      setDiscountPercentage(0)
      setDiscountReason('')
      setPaymentMethod('cash')
      setShowTransactionModal(false)
      loadTransactions() // Reload transactions to show new one
      loadOpenWashingQueue()
      
      alert(`Transaction completed! Invoice: ${invoice.id}`)

    } catch (error) {
      console.error('Error processing transaction:', error)
      alert('Transaction failed. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const clearCart = () => {
    setCart([])
    setSelectedCustomer(null)
    setDiscountPercentage(0)
    setDiscountReason('')
    setPaymentMethod('cash')
  }

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction)
  }

  const handlePrintReceipt = async (transaction) => {
    try {
      const invoice = await InvoiceGenerator.generateInvoice(transaction)
      await InvoiceGenerator.printThermalReceipt(invoice)
      alert(`Receipt for transaction ${transaction.id} printed successfully!`)
    } catch (error) {
      console.error('Error printing receipt:', error)
      alert('Failed to print receipt. Please try again.')
    }
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <ConnectionStatus />
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 text-primary mb-4">
            <ShoppingCart className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            POS
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Process sales, wash checkouts, products, and subscriptions
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <Link
              to="/pos-queue"
              className="text-sm font-medium text-primary hover:underline"
            >
              Open wash payment queue →
            </Link>
          </div>
          
          {/* Branch Selector */}
          <div className="flex justify-center">
            <div className="rounded-xl border border-border/80 bg-card shadow-sm p-4">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">Branch:</span>
                <BranchSelector />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mr-4 text-emerald-700 dark:text-emerald-400">
                <ShoppingCart className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold text-gray-900">{transactions.length}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mr-4 text-primary">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">R{transactions.reduce((sum, t) => sum + (t.total || 0), 0).toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mr-4 text-violet-700 dark:text-violet-400">
                <Calendar className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Sales</p>
                <p className="text-2xl font-bold text-gray-900">{getTodaySales().length}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mr-4 text-amber-800 dark:text-amber-400">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Sale</p>
                <p className="text-2xl font-bold text-gray-900">R{getAverageSale()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Open washes from bay check-in (awaiting payment) */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Open washing queue</h3>
              <p className="text-sm text-muted-foreground">
                Customers scanned in at the wash bay (unpaid). Open one to take payment here.
              </p>
            </div>
            <button
              type="button"
              onClick={loadOpenWashingQueue}
              disabled={loadingOpenQueue || currentBranch?.isAllBranches}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              {loadingOpenQueue ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {currentBranch?.isAllBranches ? (
            <p className="text-sm text-muted-foreground">Select a specific branch to see the open queue.</p>
          ) : openQueueWashes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No unpaid check-ins waiting for payment.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {openQueueWashes.map((w) => {
                const plate = w?.carDetails?.licensePlate
                const when = w?.createdAt?.toDate?.()
                  ? w.createdAt.toDate().toLocaleString('en-ZA', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'
                const isActive = washId === w.id
                return (
                  <div
                    key={w.id}
                    className={cn(
                      'flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between',
                      isActive ? 'border-primary bg-primary/5' : 'border-border/80'
                    )}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {w.customerName || 'Customer'}
                        {plate ? ` · ${plate}` : ''}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {w.serviceType || 'Wash'} · {when}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold tabular-nums">
                        R{Number(w.amount || 0).toFixed(2)}
                      </span>
                      <button
                        type="button"
                        onClick={() => openWashInPos(w.id)}
                        className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                      >
                        {isActive ? 'Opened' : 'Open POS'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Customer checkout request (from customer app) */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">Checkout code</label>
              <input
                value={checkoutCode}
                onChange={(e) => setCheckoutCode(e.target.value)}
                placeholder="Paste customer checkout code…"
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="mt-2 text-xs text-gray-500">
                Customers create this code from their Subscription Packages screen.
              </p>
            </div>
            <button
              onClick={loadCheckoutRequest}
              disabled={loadingCheckout || !String(checkoutCode || '').trim()}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white font-medium hover:bg-black disabled:opacity-50"
            >
              {loadingCheckout ? 'Loading…' : 'Load into cart'}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Services Selection */}
          <div className="lg:col-span-2">
            <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 text-primary">
                  <Package className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Select Services</h3>
              </div>

              {/* Category selector */}
              <div className="mb-4 flex flex-wrap gap-2">
                {categories.map((c) => {
                  const isActive = selectedCategory === c.id
                  const Icon = c.icon
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCategory(c.id)}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition',
                        isActive
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-foreground hover:bg-muted'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {c.name}
                    </button>
                  )
                })}
              </div>

              <div className="mb-3 relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  placeholder="Search washes, products, subscriptions…"
                  className="w-full rounded-xl border border-border bg-background py-2.5 pl-10 pr-10 text-sm outline-none focus:ring-2 focus:ring-ring"
                  autoComplete="off"
                />
                {serviceSearch ? (
                  <button
                    type="button"
                    onClick={() => setServiceSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {filteredItems.length} of {categoryItems.length} item
                {categoryItems.length === 1 ? '' : 's'}
                {serviceSearch.trim() ? ' matching search' : ''}
              </p>

              <div className="max-h-[min(28rem,55vh)] overflow-y-auto rounded-xl border border-border/70 bg-muted/10 p-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredItems.length === 0 ? (
                    <div className="md:col-span-2 rounded-xl border border-border/80 bg-card p-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        {serviceSearch.trim()
                          ? `No items match “${serviceSearch.trim()}”. Try another search or category.`
                          : selectedCategory === 'subscription'
                            ? 'No subscription packages found. Create packages in Subscriptions → Add Package.'
                            : selectedCategory === 'wash'
                              ? 'No wash services in the catalogue. Add them under Wash Catalog.'
                              : 'No items found in this category.'}
                      </p>
                    </div>
                  ) : (
                    filteredItems.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => addToCart(item)}
                        className="group relative rounded-xl border border-border/80 bg-card p-4 cursor-pointer shadow-sm transition hover:border-primary/30 hover:shadow-md"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                              {item.name}
                            </h4>
                            <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-lg font-bold text-blue-600">
                              {item.poa || item.meta?.poa
                                ? 'POA'
                                : `R${Number(item.price || 0).toFixed(2)}`}
                            </p>
                            {item.category === 'wash' ? (
                              <p className="text-xs text-gray-500">
                                {item.meta?.vehicleClassLabel || item.description}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500">
                                {item.category === 'subscription' ? 'Subscription' : 'Tap to add'}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cart */}
          <div className="lg:col-span-1">
            <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 sticky top-8">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mr-3 text-emerald-700 dark:text-emerald-400">
                  <ShoppingCart className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Cart</h3>
              </div>

              {/* Customer selector */}
              <div className="mb-5 rounded-xl border border-border/80 bg-muted/10 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-900">Customer</p>
                  {selectedCustomer ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null)
                        setCustomerSearch('')
                        setShowAddCustomer(false)
                      }}
                      className="text-xs font-medium text-gray-600 hover:text-gray-900"
                    >
                      Clear (walk-in)
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={openAddCustomerForm}
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      + Add customer
                    </button>
                  )}
                </div>

                {selectedCustomer ? (
                  <div className="mt-3 rounded-xl bg-white border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-900">{selectedCustomer.name}</p>
                    <p className="text-xs text-gray-600">
                      {selectedCustomer.phone || 'N/A'} {selectedCustomer.email ? `· ${selectedCustomer.email}` : ''}
                    </p>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Search by name / phone / email"
                      className="mt-3 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {customerSearch.trim() !== '' && (
                      <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white">
                        {filteredCustomers.length === 0 ? (
                          <div className="p-3 space-y-2">
                            <p className="text-sm text-gray-600">No customers found.</p>
                            <button
                              type="button"
                              onClick={openAddCustomerForm}
                              className="w-full rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
                            >
                              Add customer
                            </button>
                          </div>
                        ) : (
                          filteredCustomers.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setSelectedCustomer(c)
                                setCustomerSearch('')
                                setShowAddCustomer(false)
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                            >
                              <p className="text-sm font-medium text-gray-900">{c.name}</p>
                              <p className="text-xs text-gray-600">
                                {c.phone || 'N/A'} {c.email ? `· ${c.email}` : ''}
                              </p>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                    {showAddCustomer && (
                      <form onSubmit={createPosCustomer} className="mt-3 space-y-2 rounded-xl border border-border bg-white p-3">
                        <p className="text-xs font-semibold text-gray-900">New customer</p>
                        <input
                          required
                          type="text"
                          value={newCustomer.firstName}
                          onChange={(e) => setNewCustomer((p) => ({ ...p, firstName: e.target.value }))}
                          placeholder="Name *"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        />
                        <input
                          type="text"
                          value={newCustomer.surname}
                          onChange={(e) => setNewCustomer((p) => ({ ...p, surname: e.target.value }))}
                          placeholder="Surname"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        />
                        <input
                          required
                          type="tel"
                          value={newCustomer.phone}
                          onChange={(e) => setNewCustomer((p) => ({ ...p, phone: e.target.value }))}
                          placeholder="Mobile number *"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        />
                        <input
                          type="email"
                          value={newCustomer.email}
                          onChange={(e) => setNewCustomer((p) => ({ ...p, email: e.target.value }))}
                          placeholder="Email (optional)"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        />
                        <div className="flex gap-2 pt-1">
                          <button
                            type="submit"
                            disabled={savingCustomer}
                            className="flex-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                          >
                            {savingCustomer ? 'Saving…' : 'Save customer'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowAddCustomer(false)}
                            className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                )}
              </div>
              
              {cart.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No items in cart</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                    {cart.map((item, index) => (
                      <div key={index} className="flex items-center justify-between bg-emerald-500/5 rounded-xl p-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{item.name}</h4>
                          <p className="text-sm text-gray-600">R{item.price?.toFixed(2) || '0.00'}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-all duration-200"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">R{getCartSubtotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">VAT included (15%):</span>
                      <span className="font-medium">R{getCartTax().toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-2">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total:</span>
                        <span>R{getCartTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 space-y-3">
                    <button
                      onClick={processTransaction}
                      disabled={cart.length === 0 || isProcessing}
                      className="w-full rounded-xl bg-primary py-3 px-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isProcessing ? (
                        <>
                          <LoadingSpinner size="small" className="mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Printer className="h-5 w-5 mr-2" />
                          Complete Sale
                        </>
                      )}
                    </button>
                    <button
                      onClick={clearCart}
                      className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                    >
                      Clear Cart
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center mr-3 text-violet-700 dark:text-violet-400">
              <Clock className="h-5 w-5" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Recent Transactions</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {userProfile?.role === 'owner' && isViewingAllBranches()
                ? 'All branches'
                : `Branch: ${currentBranch?.name || userProfile?.branchId || '—'}`}
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Services</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pagedTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {txDate(transaction).toLocaleDateString('en-ZA')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.customerName || 'Walk-in Customer'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="space-y-1">
                        {transaction.items.map((item, index) => (
                          <div key={index} className="text-xs">
                            {item.name} - R{item.price?.toFixed(2) || '0.00'}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      R{transaction.total?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={cn(
                        "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                        transaction.status === 'completed' ? "bg-green-100 text-green-800" : 
                        transaction.status === 'pending' ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"
                      )}>
                        {transaction.status?.charAt(0).toUpperCase() + transaction.status?.slice(1) || 'Completed'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewTransaction(transaction)}
                          className="text-blue-600 hover:text-blue-900 hover:bg-blue-50 p-1 rounded transition-all duration-200"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handlePrintReceipt(transaction)}
                          className="text-green-600 hover:text-green-900 hover:bg-green-50 p-1 rounded transition-all duration-200"
                          title="Print Receipt"
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sortedTransactions.length > 0 && (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                Showing {(txPage - 1) * PAGE_SIZE + 1}-{Math.min(txPage * PAGE_SIZE, sortedTransactions.length)} of{' '}
                {sortedTransactions.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted/40 disabled:opacity-50"
                  onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                  disabled={txPage <= 1}
                >
                  Prev
                </button>
                <span className="text-xs text-muted-foreground">
                  Page {txPage} / {txTotalPages}
                </span>
                <button
                  type="button"
                  className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted/40 disabled:opacity-50"
                  onClick={() => setTxPage((p) => Math.min(txTotalPages, p + 1))}
                  disabled={txPage >= txTotalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          {sortedTransactions.length === 0 && (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No transactions yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Completion Modal */}
      {showTransactionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mr-3 text-emerald-700 dark:text-emerald-400">
                    <ShoppingCart className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Complete Transaction</h3>
                </div>
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Transaction Summary */}
                <div className="space-y-6">
                  <div className="bg-primary/5 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Transaction Summary</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Customer:</span>
                        <span className="font-medium">{selectedCustomer?.name || 'Walk-in Customer'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Branch:</span>
                        <span className="font-medium">{currentBranch?.name || 'Main Branch'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Items:</span>
                        <span className="font-medium">{cart.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">R{calculateSubtotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-emerald-500/5 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Cart Items</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {cart.map((item, index) => (
                        <div key={index} className="flex justify-between items-center text-sm">
                          <span>{item.name} x{item.quantity}</span>
                          <span className="font-medium">R{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column - Payment Details */}
                <div className="space-y-6">
                  {/* Sales Agent */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sales Agent
                    </label>
                    <input
                      type="text"
                      value={transactionForm.salesAgent}
                      onChange={(e) => setTransactionForm(prev => ({ ...prev, salesAgent: e.target.value }))}
                      placeholder="Enter sales agent name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  {/* Commission */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission Percentage
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={transactionForm.commissionPercentage}
                      onChange={(e) => setTransactionForm(prev => ({ ...prev, commissionPercentage: parseFloat(e.target.value) || 0 }))}
                      placeholder="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  {/* Discount Section */}
                  <div className="bg-amber-500/5 rounded-2xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Discount Options</h4>
                    
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <button
                        type="button"
                        onClick={() => setTransactionForm(prev => ({ ...prev, discountType: 'percentage' }))}
                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                          transactionForm.discountType === 'percentage'
                            ? 'bg-blue-500 text-white'
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Percentage
                      </button>
                      <button
                        type="button"
                        onClick={() => setTransactionForm(prev => ({ ...prev, discountType: 'fixed' }))}
                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                          transactionForm.discountType === 'fixed'
                            ? 'bg-blue-500 text-white'
                            : 'bg-white text-gray-700 border border-gray-300'
                        }`}
                      >
                        Fixed Amount
                      </button>
                    </div>

                    <div className="space-y-3">
                      <input
                        type="number"
                        min="0"
                        step={transactionForm.discountType === 'percentage' ? '0.1' : '0.01'}
                        value={transactionForm.discountValue}
                        onChange={(e) => setTransactionForm(prev => ({ ...prev, discountValue: parseFloat(e.target.value) || 0 }))}
                        placeholder={transactionForm.discountType === 'percentage' ? '0' : '0.00'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      
                      <input
                        type="text"
                        value={transactionForm.discountReason}
                        onChange={(e) => setTransactionForm(prev => ({ ...prev, discountReason: e.target.value }))}
                        placeholder="Discount reason (required if discount applied)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method
                    </label>
                    <select
                      value={transactionForm.paymentMethod}
                      onChange={(e) => setTransactionForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="eft">EFT</option>
                      <option value="account">On account (invoice later)</option>
                      <option value="mobile_money">Mobile Money</option>
                      <option value="voucher">Voucher</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Reference Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={transactionForm.referenceNumber}
                      onChange={(e) => setTransactionForm(prev => ({ ...prev, referenceNumber: e.target.value }))}
                      placeholder="Transaction reference (optional)"
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>

                  {/* Payment Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Notes
                    </label>
                    <textarea
                      value={transactionForm.paymentNotes}
                      onChange={(e) => setTransactionForm(prev => ({ ...prev, paymentNotes: e.target.value }))}
                      placeholder="Additional payment notes..."
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                    />
                  </div>

                  {/* Customer Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Notes
                    </label>
                    <textarea
                      value={transactionForm.customerNotes}
                      onChange={(e) => setTransactionForm(prev => ({ ...prev, customerNotes: e.target.value }))}
                      placeholder="Notes for customer..."
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Total Summary */}
              <div className="mt-8 bg-violet-500/5 rounded-2xl p-6">
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-600">Subtotal: R{calculateSubtotal().toFixed(2)}</p>
                    {transactionForm.discountValue > 0 && (
                      <p className="text-sm text-gray-600">
                        Discount ({transactionForm.discountType === 'percentage' ? `${transactionForm.discountValue}%` : 'Fixed'}): 
                        -R{transactionForm.discountType === 'percentage' 
                          ? (calculateSubtotal() * transactionForm.discountValue / 100).toFixed(2)
                          : transactionForm.discountValue.toFixed(2)
                        }
                      </p>
                    )}
                    <p className="text-lg font-bold text-gray-900">
                      Total: R{(
                        calculateSubtotal() - 
                        (transactionForm.discountType === 'percentage' 
                          ? (calculateSubtotal() * transactionForm.discountValue / 100)
                          : transactionForm.discountValue)
                      ).toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={completeTransaction}
                    disabled={isProcessing}
                    className="rounded-xl bg-primary px-8 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 flex items-center"
                  >
                    {isProcessing ? (
                      <>
                        <LoadingSpinner className="h-4 w-4 mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Complete Transaction
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mr-3 text-emerald-700 dark:text-emerald-400">
                    <ShoppingCart className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Transaction Details</h3>
                </div>
                <button
                  onClick={() => setSelectedTransaction(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-primary/5 rounded-2xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Transaction Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Transaction ID:</span>
                        <span className="font-medium">{selectedTransaction.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{(() => {
                          const date = selectedTransaction.createdAt?.toDate ? selectedTransaction.createdAt.toDate() : new Date(selectedTransaction.createdAt)
                          return date.toLocaleDateString()
                        })()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time:</span>
                        <span className="font-medium">{(() => {
                          const date = selectedTransaction.createdAt?.toDate ? selectedTransaction.createdAt.toDate() : new Date(selectedTransaction.createdAt)
                          return date.toLocaleTimeString()
                        })()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={cn(
                          "font-medium",
                          selectedTransaction.status === 'completed' ? "text-green-600" : 
                          selectedTransaction.status === 'pending' ? "text-yellow-600" : "text-red-600"
                        )}>
                          {selectedTransaction.status?.charAt(0).toUpperCase() + selectedTransaction.status?.slice(1) || 'Completed'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-emerald-500/5 rounded-2xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Customer Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-medium">{selectedTransaction.customerName || 'Walk-in Customer'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{selectedTransaction.customerPhone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{selectedTransaction.customerEmail || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900">Services</h4>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {selectedTransaction.items.map((item, index) => (
                      <div key={index} className="px-6 py-4 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                        <p className="font-medium text-gray-900">R{item.price?.toFixed(2) || '0.00'}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-violet-500/5 rounded-2xl p-6">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">R{selectedTransaction.subtotal?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">VAT included (15%):</span>
                        <span className="font-medium">R{((selectedTransaction.subtotal || 0) * (15 / 115)).toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-300 pt-2">
                        <div className="flex justify-between text-lg font-bold">
                          <span>Total:</span>
                          <span>R{selectedTransaction.total?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => handlePrintReceipt(selectedTransaction)}
                    className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 flex items-center"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print Receipt
                  </button>
                  <button
                    onClick={() => setSelectedTransaction(null)}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 